"use client";

import { useContext, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  Label,
  TextInput,
  Modal,
  Checkbox,
  Badge,
} from "flowbite-react";
import { AiOutlineClose } from "react-icons/ai";
import {
  MdTransferWithinAStation,
  MdTranslate,
  MdContactPage,
  MdGroup,
  MdPerson,
  MdExitToApp,
} from "react-icons/md";
import { FaUserTie, FaUser } from "react-icons/fa";
import { ChatContext, ChatContextInterface } from "../../../components/MSRContext"; // ⬅ adjust if needed
import { createRequestControlMessage } from "../../../utils/cmsgBuilder";
import generateRandomString from "../../../MSRP/utilities/generators";
import Global from "../../../globals/globals";
import { getAvailableAgents, Agent } from "../../../globals/availableAgents";
import { MyRequest } from "../../../models/MyRequest";
import { toast } from "react-toastify";
import type { Participant } from "@/types/chat.types";
import { useTranslation } from "react-i18next";
import { isAgentUserType, isClientUserType } from "../../../utils/userType";

type ChatBarProps = {
  open: boolean;
  onClose: () => void;
  currentRoom:any
};

export function ChatBarDrawer({ open, onClose, currentRoom }: ChatBarProps) {
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [isReasonInputOpen, setIsReasonInputOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [reason, setReason] = useState("");
  const { t } = useTranslation("chat");
  const { t: tCommon } = useTranslation("common")

  const {
    wrapper,
    agents,
    updateAgentList,
    handleSessionActivation,
    handleaddServerRequest,
  } = useContext(ChatContext) as ChatContextInterface;

  const availableAgents = useMemo(() => {
    const all = agents.length > 0 ? agents : getAvailableAgents();
    const filtered = all.filter((a) => a.status === "agent-available");
    if (!currentRoom) return filtered;
    const participants = Array.isArray(currentRoom.participants)
      ? currentRoom.participants
      : [];
    const excludedSet = new Set<string>();
    excludedSet.add(Global.username + Global.companyDomain);
    if (currentRoom.username) excludedSet.add(currentRoom.username);
    participants.forEach((p: any) => {
      if (p.exit_time === null && isAgentUserType(p.user_type) && p.details) {
        excludedSet.add(p.details.agent_username + Global.companyDomain);
      }
    });
    return filtered.filter((a) => !excludedSet.has(a.name));
  }, [agents, currentRoom]);

  const [agentsLoading, setAgentsLoading] = useState(false);

  const handleOpenTransferModal = () => {
    setAgentsLoading(true);
    updateAgentList("agent-available");
    setIsTransferModalOpen(true);
    setTimeout(() => setAgentsLoading(false), 1500);
  };

  const handleCloseTransferModal = () => {
    setIsTransferModalOpen(false);
    setSelectedAgent(null);
  };

  const handleAgentSelection = (agent: Agent) => {
    setSelectedAgent(agent);
  };

  const handleOpenReasonInput = () => {
    if (!selectedAgent) {
      toast.warning(t("chatBar.selectAgentWarning"));
      return;
    }
    setIsTransferModalOpen(false);
    setIsReasonInputOpen(true);
  };

  const handleTransferChat = () => {
    const clientUsername = currentRoom?.client?.client_username
      ? currentRoom.client.client_username + Global.companyDomain
      : "";

    if (!selectedAgent || !reason.trim()) {
      toast.warning(t("chatBar.reasonRequiredWarning"));
      return;
    }
    const targetAgent = selectedAgent.name;

    wrapper?.sendMsg(
      createRequestControlMessage(
        generateRandomString(16),
        "session-transfer",
        `from-name: ${Global.username + Global.companyDomain}, target-name: ${targetAgent}, transferee-name:${clientUsername}, reason: ${reason}, history-added: true`
      ),
      "application/rechat.cntl+json"
    );

    handleaddServerRequest?.(
      new MyRequest(
        generateRandomString(16),
        Global.username + Global.companyDomain,
        targetAgent,
        "session-transfer",
        "pending",
        {
          fromName: Global.username + Global.companyDomain,
          targetName: targetAgent,
          transfereeName: clientUsername,
          reason,
          historyAdded: true,
        },
        false,
        new Date().toISOString(),
        ""
      )
    );

    setIsReasonInputOpen(false);
    setSelectedAgent(null);
    setReason("");

    if (currentRoom) {
      handleSessionActivation(currentRoom.username, false);
    }
  };

  const handleCancelTransfer = () => {
    setIsReasonInputOpen(false);
    setReason("");
    setSelectedAgent(null);
  };

  const rawTitle =
    currentRoom?.type === "agent-to-agent" || currentRoom?.type === "c2a"
      ? currentRoom?.username.split("@")[0]
      : currentRoom?.isGroup
      ? currentRoom?.username.replace("grpcht:", "")
      : currentRoom?.username?.replace("conf:", "");

  const title = rawTitle?.replace(/^a2a:?/, "");

  return (
    <>
      {/* Drawer panel */}
      <div
        className={`fixed inset-0 z-40 transition-transform duration-300 ease-in-out ${
          open ? "translate-x-0" : "translate-x-full"
        } pointer-events-none`}
      >
        <div className="absolute right-0 h-full bg-white shadow-lg pointer-events-auto w-80 dark:bg-gray-800">
          <div className="flex items-center justify-between p-4 border-b dark:border-gray-600">
            <h2 className="text-lg font-semibold dark:text-white">{t("chatDrawer.title")}</h2>
            <button onClick={onClose}>
              <AiOutlineClose className="w-5 h-5 dark:text-white" />
            </button>
          </div>

          <div className="p-4 space-y-4 overflow-y-auto h-full pb-20">
            <div className="flex items-center mb-4">
              <Avatar
                alt="User avatar"
                img="/images/users/thomas-lean.png" // ⬅ remove /public prefix for Next.js public/
                rounded
                size="md"
                className="mr-3"
              />
              <div>
                <p className="font-medium dark:text-white">{title}</p>
              </div>
            </div>

            {/* Participants Section */}
            {Array.isArray(currentRoom?.participants) && currentRoom.participants.length > 0 && (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
                  <div className="flex items-center mb-3">
                    <MdGroup className="w-5 h-5 mr-2 text-gray-600 dark:text-gray-300" />
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                       {t("chatDrawer.participants")}  ({currentRoom.participants.length})
                    </h3>
                  </div>
                  <div
                    className={`space-y-2 ${
                      currentRoom.participants.length > 7
                        ? "max-h-64 overflow-y-auto"
                        : ""
                    }`}
                  >
                    {currentRoom.participants.map((participant: Participant) => {
                      const isClient = isClientUserType(participant.user_type);
                      const isAgent = isAgentUserType(participant.user_type);
                      const hasExited = participant.exit_time !== null;
                      const isAdmin = participant.is_admin === 1;

                      // Get display name
                      const details = participant.details as any;
                      const displayName = isClient
                        ? details?.client_username
                        : details?.agent_username;

                      return (
                        <div
                          key={participant.user_id}
                          className={`flex items-center p-2 bg-white dark:bg-gray-600 rounded-md shadow-sm hover:shadow-md transition-shadow duration-200 ${
                            hasExited ? 'opacity-60' : ''
                          }`}
                        >
                          <div className={`flex items-center justify-center w-8 h-8 rounded-full mr-3 flex-shrink-0 ${
                            isClient
                              ? 'bg-green-100 dark:bg-green-900'
                              : 'bg-blue-100 dark:bg-blue-900'
                          }`}>
                            {isClient ? (
                              <FaUser className="w-4 h-4 text-green-600 dark:text-green-300" />
                            ) : (
                              <FaUserTie className="w-4 h-4 text-blue-600 dark:text-blue-300" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {participant.is_current_user ? t("chatDrawer.youLabel") : displayName}
                              </p>
                              {isAdmin && (
                                <Badge color="purple" size="xs">{t("chatDrawer.adminBadge")}</Badge>
                              )}
                              {isClient && (
                                <Badge color="success" size="xs">{t("chatDrawer.clientBadge")}</Badge>
                              )}
                              {isAgent && (
                                <Badge color="info" size="xs">{t("chatDrawer.agentBadge")}</Badge>
                              )}
                            </div>
                            {hasExited && (
                              <div className="flex items-center gap-1 mt-1">
                                <MdExitToApp className="w-3 h-3 text-red-500" />
                                <span className="text-xs text-red-500 dark:text-red-400">{t("chatDrawer.exit")}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            <div className="space-y-3">
              {currentRoom?.session_type === "c2a" && (
                  <Button
                    color={"chatbar"}
                    className="w-full"
                    onClick={handleOpenTransferModal}
                    disabled={!currentRoom?.isSessionActive || currentRoom?.session_status === "inactive"}
                  >
                    <MdTransferWithinAStation className="w-5 h-5 mr-2" />
                    {/* {t("chatBar.transferChat")} */}
                  </Button>
                )}
              <Button color="chatbar" className="w-full">
                <MdTranslate className="w-5 h-5 mr-2" />
                {/* {t("chatBar.translateChat")} */}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Select Agent Modal */}
      <Modal
        onClose={handleCloseTransferModal}
        popup
        show={isTransferModalOpen}
        size="xl"
        theme={{
          content: {
            inner:
              "relative flex max-h-[90vh] flex-col rounded-lg bg-white shadow dark:bg-gray-800",
          },
        }}
      >
        <Modal.Header>{t("selectAgentModal.title")}</Modal.Header>
        <Modal.Body>
          {agentsLoading && availableAgents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mb-4"></div>
              <legend className="text-gray-600 dark:text-gray-400">
                Loading agents...
              </legend>
            </div>
          ) : availableAgents.length === 0 ? (
            <legend className="mb-4 text-gray-600 dark:text-gray-400">
              {t("selectAgentModal.noAvailableAgents")}
            </legend>
          ) : (
            <fieldset className="flex flex-col max-w-md gap-4 my-6">
              {availableAgents.map((agent: Agent) => (
                <div key={agent.id} className="flex items-center gap-2">
                  <Checkbox
                    id={String(agent.id)}
                    name={agent.name}
                    value={agent.name}
                    checked={selectedAgent?.id === agent.id}
                    onChange={() => handleAgentSelection(agent)}
                  />
                  <Label htmlFor={String(agent.id)}>
                    {agent.name.split("@")[0]}
                  </Label>
                </div>
              ))}
            </fieldset>
          )}
          <div className="flex items-center justify-center space-x-4">
            {availableAgents.length > 0 && (
              <Button onClick={handleOpenReasonInput}>{tCommon("next")}</Button>
            )}
            <Button color="primary" type="submit" onClick={handleCloseTransferModal}>
              {tCommon("cancel")}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Reason Modal */}
      <Modal
        onClose={handleCancelTransfer}
        popup
        show={isReasonInputOpen}
        size="xl"
        theme={{
          content: {
            inner:
              "relative flex max-h-[90vh] flex-col rounded-lg bg-white shadow dark:bg-gray-800",
          },
        }}
      >
        <Modal.Header>
          {t("selectAgentModal.transferChatTo")}{" "} {selectedAgent?.name.split("@")[0]}
        </Modal.Header>
        <Modal.Body>
          <div className="my-6">
            <Label
              htmlFor="reason"
              className="block mb-2 text-gray-900 dark:text-white"
            >
               {t("selectAgentModal.transferReasonLabel")}
            </Label>
            <TextInput
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("selectAgentModal.transferReasonPlaceholder")}
              className="w-full"
              required
            />
          </div>
          <div className="flex items-center justify-center space-x-4">
            <Button onClick={handleTransferChat}>{t("selectAgentModal.transferChat")}</Button>
            <Button color="primary" type="submit" onClick={handleCancelTransfer}>
              {tCommon("cancel")}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
